import React from "react";
import { MapPin, Star, Phone, Globe, ArrowDown } from "lucide-react";
import { Button } from "../ui/button";

// Hero for the public Customer Booking Page mini-site. Renders the business
// identity + trust signals. Every optional line disappears completely when
// the owner hasn't provided the data — legacy pages stay clean.
// Desktop gets a substantial, premium composition; mobile keeps the compact
// stacked layout that already converts well.
export const PublicBusinessHero = ({
  business,
  t,
  onGetQuote,
  tagline: taglineProp,
  serviceArea: serviceAreaProp,
}) => {
  if (!business) return null;
  const tagline = ((taglineProp ?? business.booking_tagline) || "").trim();
  const serviceArea = ((serviceAreaProp ?? business.booking_service_area) || "").trim();
  const city = (business.booking_city || "").trim();
  const state = (business.booking_state || "").trim();
  const locationLine = serviceArea || [city, state].filter(Boolean).join(", ");
  const hasRating =
    business.google_rating != null && business.google_review_count != null;
  const publicPhone = (business.public_phone || "").trim();
  const websiteUrl = (business.website_url || "").trim();
  const logoUrl = (business.booking_logo_url || "").trim();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-green-100 bg-gradient-to-br from-emerald-50/80 via-white to-white p-6 sm:p-10 lg:px-14 lg:py-16 mb-5 lg:mb-8"
      data-testid="public-hero-section"
    >
      {/* Restrained decorative accent — desktop only, purely visual. */}
      <div
        className="hidden lg:block absolute -top-28 -right-28 w-96 h-96 rounded-full bg-emerald-100/50 blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div className="relative lg:flex lg:items-start lg:gap-8">
        {logoUrl && (
          <img
            src={`${BACKEND_URL}${logoUrl}`}
            alt={`${business.business_name} logo`}
            className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl border border-green-100 bg-white object-contain p-1.5 shadow-sm mb-4 lg:mb-0 lg:shrink-0"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid="public-hero-logo"
          />
        )}
        <div className="min-w-0">
        <h1
          className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold text-gray-900 tracking-tight"
          data-testid="public-hero-business-name"
        >
          {business.business_name}
        </h1>
        {tagline && (
          <p
            className="mt-2.5 lg:mt-4 text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed lg:max-w-2xl"
            data-testid="public-hero-tagline"
          >
            {tagline}
          </p>
        )}
        {(locationLine || hasRating) && (
          <div className="mt-4 lg:mt-6 flex flex-wrap items-center gap-x-5 gap-y-2">
            {locationLine && (
              <span
                className="inline-flex items-center gap-1.5 text-sm lg:text-base text-gray-600"
                data-testid="public-hero-location"
              >
                <MapPin className="w-4 h-4 text-green-800 shrink-0" />
                {locationLine}
              </span>
            )}
            {hasRating && (
              <span
                className="inline-flex items-center gap-1.5 text-sm lg:text-base text-gray-700"
                data-testid="public-hero-rating"
              >
                <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
                <span className="font-semibold">{business.google_rating}</span>
                <span className="text-gray-400">·</span>
                <span>{t("public.hero.reviews", business.google_review_count)}</span>
              </span>
            )}
          </div>
        )}
        {(publicPhone || websiteUrl) && (
          <div className="mt-4 lg:mt-6 flex flex-wrap gap-2" data-testid="public-hero-contact">
            {publicPhone && (
              <a
                href={`tel:${publicPhone.replace(/[^\d+]/g, "")}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-green-200 bg-white text-sm text-gray-700 hover:bg-green-50 transition-colors"
                data-testid="public-hero-phone"
              >
                <Phone className="w-3.5 h-3.5 text-green-800" /> {publicPhone}
              </a>
            )}
            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-green-200 bg-white text-sm text-gray-700 hover:bg-green-50 transition-colors"
                data-testid="public-hero-website"
              >
                <Globe className="w-3.5 h-3.5 text-green-800" /> {t("public.hero.website")}
              </a>
            )}
          </div>
        )}
        <Button
          onClick={onGetQuote}
          className="mt-6 lg:mt-8 h-12 px-7 lg:px-9 bg-green-800 hover:bg-green-900 text-white text-base font-semibold w-full sm:w-auto"
          data-testid="public-hero-get-quote-button"
        >
          {t("public.hero.getQuote")} <ArrowDown className="w-4 h-4 ml-1.5" />
        </Button>
        </div>
      </div>
    </section>
  );
};
